/**
 * SessionRuntime — per-session in-memory bookkeeping for the cowork hook.
 *
 * Before: useCoworkSession held six parallel `Map<sessionId, T>` refs
 * (cleanup, channelId, assistantMsgId, contentLength, dedupUuids, drainTimer).
 * Every session-lifecycle site (result handler, cancelStream, removeConversation,
 * unmount) had to remember to delete from all six — drift was a bug risk.
 *
 * Now a single map keyed by sessionId holds one struct per session, and
 * `teardown(sid)` handles listener-cleanup + timer-cancel + struct-delete
 * in one place.
 *
 * This is NOT zustand / store state. It's render-invisible per-session
 * infrastructure (IPC listener handles, inflight timer ids, dedup sets).
 * Putting it in a store would trigger unnecessary re-renders whenever a
 * stream tick bumps contentLength.
 */

export interface SessionRuntime {
  /** Current `cowork:stream:<channelId>` channel for this session. */
  channelId?: string
  /** IPC listener unsubscribe fn returned by `api.onStream`. */
  cleanup?: () => void
  /** The assistant message id currently being streamed into. */
  assistantMsgId?: string
  /** Accumulated text-delta length (drives UI progress, backed by SDK delta bytes). */
  contentLength: number
  /** SDK message uuids already processed — guards against SDK replay-on-reconnect. */
  dedupUuids: Set<string>
  /** Debounce timer for mid-stream queue drain, per-session. */
  drainTimer?: ReturnType<typeof setTimeout>
  /**
   * Listener unsubscribes deferred past the turn's `result`. A dynamic Workflow
   * runs as a background task: after the agent launches it (even fire-and-forget,
   * ending the turn), the SDK query stays open and keeps emitting that task's
   * progress/notification — AND, once it completes, a task-notification that wakes
   * the agent into a RESUME turn — all on THIS turn's channel. The listener must
   * outlive the turn to receive them; it's drained by the resume turn's own
   * `result` (teardown), or by teardown/teardownAll as a backstop.
   */
  deferredCleanups?: Array<() => void>
  /**
   * Set when a turn's `result` was deferred because a fire-and-forget workflow is
   * still running. Signals the stream handler that the NEXT assistant turn (the
   * SDK-woken resume after the workflow's task-notification) must open its OWN
   * message bubble instead of appending to the just-finished one.
   */
  resumePending?: boolean
}

/**
 * Mutable per-session runtime registry. Backs useCoworkSession's core refs
 * with one struct instead of six parallel maps.
 */
export interface SessionRuntimeRegistry {
  /** Get runtime for a session, creating an empty one if absent. */
  get: (sessionId: string) => SessionRuntime
  /** Get runtime without creating — returns undefined if absent. */
  peek: (sessionId: string) => SessionRuntime | undefined
  /** Clean up and delete one session's runtime (listener + timer). */
  teardown: (sessionId: string) => void
  /**
   * Keep this session's stream listener alive past its turn's `result` because a
   * background workflow is still emitting task events on that channel. Moves the
   * listener unsubscribe into `deferredCleanups` and cancels the drain timer, but
   * keeps the struct (and the live IPC subscription) around.
   */
  deferListenerCleanup: (sessionId: string) => void
  /** Iterate all runtimes (for unmount cleanup). */
  forEach: (fn: (runtime: SessionRuntime, sessionId: string) => void) => void
  /** Tear down every session's runtime. Used on hook unmount. */
  teardownAll: () => void
}

export function createSessionRuntimeRegistry(): SessionRuntimeRegistry {
  const runtimes = new Map<string, SessionRuntime>()

  const get = (sessionId: string): SessionRuntime => {
    let r = runtimes.get(sessionId)
    if (!r) {
      r = { contentLength: 0, dedupUuids: new Set() }
      runtimes.set(sessionId, r)
    }
    return r
  }

  const peek = (sessionId: string): SessionRuntime | undefined => runtimes.get(sessionId)

  const teardown = (sessionId: string): void => {
    const r = runtimes.get(sessionId)
    if (!r) return
    r.cleanup?.()
    r.deferredCleanups?.forEach((cb) => cb())
    if (r.drainTimer) clearTimeout(r.drainTimer)
    runtimes.delete(sessionId)
  }

  const deferListenerCleanup = (sessionId: string): void => {
    const r = runtimes.get(sessionId)
    if (!r) return
    if (r.cleanup) {
      if (!r.deferredCleanups) r.deferredCleanups = []
      r.deferredCleanups.push(r.cleanup)
      r.cleanup = undefined
    }
    if (r.drainTimer) { clearTimeout(r.drainTimer); r.drainTimer = undefined }
  }

  const forEach = (fn: (r: SessionRuntime, sid: string) => void): void => {
    for (const [sid, r] of runtimes) fn(r, sid)
  }

  const teardownAll = (): void => {
    for (const r of runtimes.values()) {
      r.cleanup?.()
      r.deferredCleanups?.forEach((cb) => cb())
      if (r.drainTimer) clearTimeout(r.drainTimer)
    }
    runtimes.clear()
  }

  return { get, peek, teardown, deferListenerCleanup, forEach, teardownAll }
}
